/**
 * VRG-701 — CLIENT TIERS 1–5: the size of a brand as Vera's business model counts it (vera-inc
 * `company/business_model.md`, deck slide 20). Replaces the sales company's free "Size" field.
 *
 * ONE definition. vera-2 already had these tiers — the demo flow's `DEMO_TIER_OPTIONS`
 * (`apps/platform/src/constants/demos.ts`, `demo_provisioning_audit.send_tier`, keys `tier_1`…
 * `tier_5`) — so the same keys and figures are reused here and that list now derives from this
 * one rather than a second copy existing.
 *
 *   Tier  Employees (proxy)        Certs / units a year (typical)   Production range (cut-offs)
 *   1     5,000+                   ~1,000,000                        ≥ 300K
 *   2     1K–5K                    ~100,000                          30K – 300K
 *   3     100–1K                   ~10,000                           7K – 30K
 *   4     20–100                   ~5,000                            1K – 7K
 *   5     under 20                 ~250 (wide range)                 < 1K
 *
 * TIERS ARE SIZE AND PRODUCTION ONLY (Spencer, 2026-10-16, VRG-715). Tiers 3–5 used to carry an
 * "on Shopify" qualifier, in the labels and as a `shopify` flag; both are gone. How a brand sells
 * is not part of its tier.
 *
 * ⚠️ TIER 2 IS 100K, NOT 10K. The deck prints Tier 2 as "10K" certs a year; the canon treats that
 * as a typo for 100K (10K would equal Tier 3 while showing 7× its ARR), and `demos.ts` records the
 * same correction. 100K is used here.
 *
 * Tier 5's typical figure is ~250 a year (Spencer, 2026-10-14; was the demo list's ~500): individual
 * artists and creators, a wide range. The business model gives none (Tier 5 is outside the
 * 27,107-company TAM).
 *
 * The PRODUCTION CUT-OFFS were approved by Spencer on 2026-10-14. They are roughly the geometric
 * midpoints between neighbouring typical values (√(1M·100K) ≈ 316K → 300K; √(100K·10K) ≈ 31.6K →
 * 30K; √(10K·5K) ≈ 7.1K → 7K; √(5K·250) ≈ 1.1K → 1K), rounded. They are what `suggestTier` uses.
 *
 * EMPLOYEES ARE ONLY A PROXY; production wins (`demos.ts`'s rule): a 60-person house making 400K
 * units a year is Tier 1 by production. So the tier is SUGGESTED from annual production when the
 * ROI calculator has it, never forced.
 */
import type {DealPlan} from './sales-pipeline.js';

export const CLIENT_TIERS = ['tier_1', 'tier_2', 'tier_3', 'tier_4', 'tier_5'] as const;
export type ClientTier = (typeof CLIENT_TIERS)[number];
export const isClientTier = (v: unknown): v is ClientTier => typeof v === 'string' && (CLIENT_TIERS as readonly string[]).includes(v);

export interface ClientTierMeta {
  number: 1 | 2 | 3 | 4 | 5;
  /** "5K+", "1K–5K"… as the canon writes it. */
  employees: string;
  /** Typical certificates / units a year. */
  typical_units: number;
  /** Annual production range, inclusive min, exclusive max (null = unbounded). Approved 2026-10-14. */
  production: {min: number; max: number | null};
  /** Who the tier is, where the figures alone do not say it (the dropdown's second line). */
  note?: string;
}

/** Approved by Spencer 2026-10-14 (see the header). */
export const CLIENT_TIER_META: Readonly<Record<ClientTier, ClientTierMeta>> = {
  tier_1: {number: 1, employees: '5K+', typical_units: 1_000_000, production: {min: 300_000, max: null}},
  // 100K, not the deck's "10K" (a typo; see the header).
  tier_2: {number: 2, employees: '1K–5K', typical_units: 100_000, production: {min: 30_000, max: 300_000}},
  tier_3: {number: 3, employees: '100–1K', typical_units: 10_000, production: {min: 7_000, max: 30_000}},
  tier_4: {number: 4, employees: '20–100', typical_units: 5_000, production: {min: 1_000, max: 7_000}},
  // ~250, not 500 (Spencer, 2026-10-14).
  tier_5: {number: 5, employees: 'under 20', typical_units: 250, production: {min: 0, max: 1_000}, note: 'Individual artists and creators: wide range'},
};

const compact = (n: number): string => (n >= 1_000_000 ? `${n / 1_000_000}M` : n >= 1_000 ? `${n / 1_000}K` : String(n));

/** "Tier 3". */
export const clientTierLabel = (t: ClientTier): string => `Tier ${CLIENT_TIER_META[t].number}`;

/** "7K–30K/yr", "≥ 300K/yr", "< 1K/yr". */
export const clientTierProductionLabel = (t: ClientTier): string => {
  const {min, max} = CLIENT_TIER_META[t].production;
  if (max === null) return `≥ ${compact(min)}/yr`;
  if (min === 0) return `< ${compact(max)}/yr`;
  return `${compact(min)}–${compact(max)}/yr`;
};

/**
 * The dropdown row: "Tier 3 · 100–1K employees · ~10K/yr (7K–30K/yr)". The demo flow's
 * shorter `demoTierLabel` keeps its own established wording from the same figures.
 */
export const clientTierOptionLabel = (t: ClientTier): string => {
  const m = CLIENT_TIER_META[t];
  return `${clientTierLabel(t)} · ${m.employees} employees · ~${compact(m.typical_units)}/yr (${clientTierProductionLabel(t)})`;
};

/** The demo flow's label ("Tier 3 · ~10K units/yr · 100–1K employees"), from the same figures. */
export const demoTierLabel = (t: ClientTier): string => {
  const m = CLIENT_TIER_META[t];
  return `${clientTierLabel(t)} · ~${compact(m.typical_units)} units/yr · ${m.employees} employees`;
};

/** The tier an annual production figure falls in, or null for no / an unusable figure. */
export const suggestTier = (annualUnits: number | null | undefined): ClientTier | null => {
  if (typeof annualUnits !== 'number' || !Number.isFinite(annualUnits) || annualUnits < 0) return null;
  for (const t of CLIENT_TIERS) {
    const {min, max} = CLIENT_TIER_META[t].production;
    if (annualUnits >= min && (max === null || annualUnits < max)) return t;
  }
  return null;
};

/**
 * The DEFAULT plan a tier is expected to buy (VRG-701), approved by Spencer 2026-10-14. Used ONLY
 * to price a deal's potential subscription when no plan is set on the deal (or by its Stripe
 * subscription, or its signup form): a plan set on the deal always overrides it. Enterprise is
 * contact-sales with no list price, so a Tier 1–2 deal is priced as Scale ($999 a month) and
 * shown as "at least" that figure (`plan_substituted`).
 */
export const TIER_DEFAULT_PLAN: Readonly<Record<ClientTier, DealPlan>> = {
  tier_1: 'enterprise',
  tier_2: 'enterprise',
  tier_3: 'scale',
  tier_4: 'business',
  tier_5: 'individual',
};

/**
 * VRG-736 — the share of a tier's annual production expected to ACTIVATE (Spencer, 2026-10-22).
 * A deal's expected activations are its annual production × this share, unless the deal sets its
 * own ("Share of production activated" on Edit deal, `sales_deals.activation_share`).
 *
 * SOURCE: the investor projections model (vera-inc `investors/` projections sheet, row 39, as
 * recorded in the data-room Q&A): "Tier 1/2: 20% product penetration assumed (pilot). Tier 3/4:
 * full adoption." A Tier 1–2 house pilots Vera on a slice of its range first; a smaller brand puts
 * it on everything. Tier 5 is outside the model's TAM and so is not in that row; it is set to full
 * adoption like Tier 3–4 (Spencer's brief: "Tier 3–5 = 100%").
 *
 * Ratios 0–1. THIS IS THE ONLY COPY of the defaults: the API, the form's placeholder and the
 * tooltip all read it (`sales-predicted-value.guard.spec.ts`).
 */
export const DEFAULT_ACTIVATION_SHARE_BY_TIER: Readonly<Record<ClientTier, number>> = {
  tier_1: 0.2,
  tier_2: 0.2,
  tier_3: 1,
  tier_4: 1,
  tier_5: 1,
};

/**
 * The old free "Size" values → a tier, ONLY where the whole range sits in one tier's employee
 * band (migration 0157 does the same in SQL; the guard compares them). Bands: < 20 → 5, 20–99 → 4,
 * 100–1,000 → 3, 1,001–4,999 → 2, 5,000+ → 1. The canon's "100–1K" and "1K–5K" share the 1,000
 * edge; LinkedIn-style buckets ("501-1,000", then "1,001-5,000") put 1,000 on the lower side,
 * so 1,000 is Tier 3. "11-50" (spans 5 and 4), "51-200" (4 and 3) and "1,000+" (2 and 1) are
 * ambiguous and stay unset.
 */
export const employeeBandTier = (n: number): ClientTier =>
  n < 20 ? 'tier_5' : n < 100 ? 'tier_4' : n <= 1000 ? 'tier_3' : n < 5000 ? 'tier_2' : 'tier_1';

export const tierFromSize = (size: string | null | undefined): ClientTier | null => {
  const s = (size ?? '').toLowerCase().replace(/[,\s]|people|employees/g, '');
  let m = s.match(/^(\d+)-(\d+)$/);
  if (m) {
    const a = employeeBandTier(Number(m[1]));
    return a === employeeBandTier(Number(m[2])) ? a : null;
  }
  m = s.match(/^(\d+)\+$/);
  if (m) return employeeBandTier(Number(m[1])) === 'tier_1' ? 'tier_1' : null;
  m = s.match(/^(\d+)$/);
  return m ? employeeBandTier(Number(m[1])) : null;
};
